import axios from "axios";
import dayjs from '../dayjs';
import authConfig, { IAuthConfig } from "./authConfig";
import {
    IAccountInfo, IAccountEnvelope, ILoginRequest, ILogoutRequest, IRefreshRequest, ISwitchTenantRequest
} from "./IAccountInfo";
import { IResetPasswordRequest, IResetPasswordEnvelope, IConfirmPasswordRequest, IConfirmPasswordEnvelope }  from "./ResetPassword";
import { IAuthProvider, IPolicyRoles } from "./IAuthProvider";
import log, { setLogToken } from "../logging";
import i18n, {getLanguageHeader} from "../../locales/i18n";
import tracing from '../tracingContext';
import appCache from '../cache'
import { imageCache } from "../dataCache";
import store from "../../store/store"
import { save as saveAction } from "../cache/actions"

export const roles: IPolicyRoles = {
    SaaSAdministrator: "SaaS Administrator",
    TenantAdministrator: "Organization Administrator",
    StaffManager: "Staff Manager",
    DataGateway: "Data Gateway",
    Staff: "Staff",
}

class AuthProvider implements IAuthProvider {
    readonly accountInfoKey: string = "AuthAccountInfo";
    readonly deviceInfoKey: string = "AuthDeviceInfo";
    readonly hardwareInfoKey: string = "Identifier";
    readonly Roles: IPolicyRoles = roles;

    public getAssociatedRoles = (role: string): string[] => {
            if(this.Roles.SaaSAdministrator === role){
                return [
                    this.Roles.SaaSAdministrator, 
                    this.Roles.TenantAdministrator, 
                    this.Roles.StaffManager, 
                    this.Roles.DataGateway, 
                    this.Roles.Staff];
            }

            if(this.Roles.TenantAdministrator === role){
                return [                 
                    this.Roles.TenantAdministrator, 
                    this.Roles.StaffManager, 
                    this.Roles.DataGateway, 
                    this.Roles.Staff];
            }
            
            if(this.Roles.StaffManager === role){
                return [
                    this.Roles.StaffManager, 
                    this.Roles.DataGateway, 
                    this.Roles.Staff];
            }

        return [role];
    }

    public hasRole = (role: string): boolean => {
        const account = this.getAccountInfo();
        return !account || this.getAssociatedRoles(account.role).some(r => r === role);
    }

    private accountInfo: IAccountInfo | null;

    constructor(private authConfig: IAuthConfig, public apiUrl: string) {
        this.loadAccountInfo();
    }

    public isAuthenticated = () => {        
        const accountInfo = this.getAccountInfo();
        return accountInfo != null && this.getTokenLeftTimeMin(accountInfo) > 0;
    }

    public getAccountInfo(): IAccountInfo | null {
        return this.accountInfo || this.loadAccountInfo();
    }

    public login(email: string, password: string): Promise<any> {

        const request: ILoginRequest = {
            user: { email, password }
        };

        return new Promise<any>((resolve, reject) => {
            axios.post(`${this.apiUrl}/${this.authConfig.loginEndpoint}`, request, {
                headers: {
                    ...getLanguageHeader()
                }
            })
            .then(r => {
                if (r.status === 200) {
                    const accountInfo: IAccountInfo = r.data.accountInfo;
                    this.setAccountInfo(accountInfo);
                    tracing.startNewSession(accountInfo.email);
                    setLogToken(accountInfo.token.token)
                    log.info(`${accountInfo.email} has logged in`);
                    appCache.loadInitialStateFromAPI(true).then(() => {
                        resolve("Success")
                    })
                }
                else {                    
                    reject(new Error(`Couldn't login by '${email}': ${r}`));
                };
            })
            .then(r => imageCache.clear())
            .catch(error => {
                log.error('Authentication error', error);
                reject(error);
            });
        })
    }

    public logout(): Promise<any> {
        const request: ILogoutRequest = {
            logout: {}
        }
        const loginUrl = ""
        return new Promise((resolve, reject) => {
            try {
                appCache.clearCache()
                resolve("Success");
            }
            catch(error){
                reject(error);
            }
        }).then((r) => {            
            const accountInfo = this.getAccountInfo()
            this.setAccountInfo(null)
            if(!!accountInfo) log.info(`${accountInfo.email} has logged out`);
            if (accountInfo != null && this.getTokenLeftTimeMin(accountInfo) > 0) {
                return axios.post(`${this.apiUrl}/${this.authConfig.logoutEndpoint}`, request, {
                    headers: {
                        Authorization: 'Bearer ' + accountInfo.token.token
                    }
                })
                .then(r => {
                    setLogToken(null)
                    window.location.href = loginUrl
                })
                .catch(error => {
                    log.error(`Logout error`, error);
                    setLogToken(null)
                    window.location.href = loginUrl
                });
            }
            else {
                setLogToken(null)
                window.location.href = loginUrl
            }
            return r;
        })
        .catch((error) => {
            log.error(`Logout error`, error);
            setLogToken(null)
        });
    }

    public sendResetPassword(email: string): Promise<boolean> {

        const request: IResetPasswordRequest = {
            resetPassword : {
                email,
                language: i18n.language,
                resetUrl: this.getResetUrl()
            }
        };

        return axios.post(`${this.apiUrl}/${this.authConfig.resetPasswordEndpoint}`, request, {
                headers: {                     
                    ...getLanguageHeader()
                }
            })
            .then(r => {
                if (r.status === 200) {
                    const resetInfo: IResetPasswordEnvelope = r.data;
                    return resetInfo.resetPasswordInfo.requestSent;
                }
                else
                    throw new Error(`Couldn't send reset password for '${email}': ${r}`);
            })
            .catch(error => {
                log.error('Send reset password request error', error);
                throw error;
            });
    }

    public getResetUrl() : string {
        return `${window.location.origin}/confirm-password/{0}`;
    }

    public confirmResetPassword(email: string, password: string, hash: string): Promise<boolean> {

        const request: IConfirmPasswordRequest = {
            confirmPassword: {
                email,
                password: password,
                hash: hash
            }
        };

        return axios.post(`${this.apiUrl}/${this.authConfig.confirmPasswordEndpoint}`, request, {
                headers: {                     
                    ...getLanguageHeader()
                }
            })
            .then(r => {                
                if (r.status === 200) {                    
                    const resetInfo: IConfirmPasswordEnvelope = r.data;
                    log.info(`Password for '${email}' has${resetInfo.confirmPasswordInfo ? "" : " not"} been reset`);
                    return resetInfo.confirmPasswordInfo.passwordReset;
                }
                else
                    throw new Error(`Couldn't reset password for '${email}': ${r}`);
            })
            .catch(error => {
                log.error('Reset password error', error);
                throw error;
            });
    }

    public switchTenant(tenantId: string): Promise<any> {
        const request: ISwitchTenantRequest = {
            data: { tenantId }
        }
        return axios.post(`${this.apiUrl}/${this.authConfig.switchTenantEndpoint}`, request, {
            headers: {
                Authorization: 'Bearer ' + this.getAccountInfo()?.token.token
            }
        }).then(r => {
                if (r.status === 200) {
                    const accountInfo = r.data.accountInfo as IAccountInfo;
                    this.setAccountInfo(accountInfo);
                    appCache.clearCache().then(() => {
                        store.dispatch(saveAction({}))
                        appCache.loadInitialStateFromAPI().then(() => {
                            tracing.startNewSession(accountInfo.email);
                            log.info(`[${accountInfo.email}] has switched tenants to [${accountInfo.tenantId}]`);
                        })
                    })
                }
                else throw new Error(`Couldn't switch tenants [${this.accountInfo?.email}] to [${tenantId}]: ${r}`);
            })
            .catch(error => {
                log.error('Switch tenant error', error);
                throw error;
            });
    }

    public getAccessToken(): Promise<string | null> {
        const accountInfo = this.getAccountInfo();

        if (accountInfo != null) {
            const diffInMinutes = this.getTokenLeftTimeMin(accountInfo);

            if (diffInMinutes <= 0) {
                // expired
                this.setAccountInfo(null);
            } else if (diffInMinutes < this.authConfig.refreshThresholdMin) {
                // need refresh
                const request: IRefreshRequest = { refresh: {} };
                return axios.post(`${this.apiUrl}/${this.authConfig.refreshEndpoint}`, request,
                        {
                            headers: { Authorization: 'Bearer ' + accountInfo.token.token }
                        })
                    .then(r => {
                        if (r.status === 200) {
                            const accountResponse: IAccountEnvelope = r.data;
                            this.setAccountInfo(accountResponse.accountInfo);
                            return accountResponse.accountInfo.token.token;
                        }
                        else
                            throw new Error(`Couldn't refresh login for '${accountInfo.email}': ${r}`);
                    })
                    .catch(error => {
                        log.error('Authentication error', error);
                        return null;
                    });
            }
        }

        return new Promise<string | null>(resolve => {
            resolve(accountInfo != null ? accountInfo.token.token : null);
        });
    }

    private getTokenLeftTimeMin(accountInfo: IAccountInfo): number {

        const expiration = dayjs(accountInfo.token.expiration);
        const duration = dayjs.duration(expiration.diff(dayjs.utc()));
        const diffInMinutes = duration.asMinutes();
        return diffInMinutes;
    }

    private loadAccountInfo(): IAccountInfo | null {
        const strAccount = localStorage.getItem(this.accountInfoKey);
        try {
            const account = strAccount !== null
                ?
                JSON.parse(strAccount) as IAccountInfo
                : null;
            
            return account;
        } catch (e) {
            log.error("Couldn't parse AccountInfo state", e);
            localStorage.removeItem(this.accountInfoKey);
            return null;
        }
    }

    private setAccountInfo(accountInfo: IAccountInfo | null): void {
        this.accountInfo = accountInfo;
        if(accountInfo)
            localStorage.setItem(this.accountInfoKey, JSON.stringify(accountInfo));
        else
            localStorage.removeItem(this.accountInfoKey);
    }
}

export const PolicyRoles = roles

export const authProvider = new AuthProvider(authConfig, process.env.REACT_APP_API_URI);
